import {SessionPromptDefinitionError} from './sessionDialogueDefinition';

const prefix='vivaassess:session-task-recovery:v1:';
const ownerKey=prefix+'owner';
const generationKey=prefix+'generation';
const recordPrefix=prefix+'request:';
const applicationPrefix=prefix+'application:';
const uuid=/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
export type TaskRecoveryRecord={version:1;lane:string;fingerprint:string;requestId:string;owner:string|null;generation:string};
export type RecoveryStorage=Pick<Storage,'getItem'|'setItem'|'removeItem'|'key'|'length'>;
let localGeneration=0;
const closedSessions=new Set<string>();
let expectedOwner:string|null|undefined;
function unavailable():never{throw new SessionPromptDefinitionError('Session request recovery is unavailable. Retry when browser storage is available.');}
export function browserTaskRecoveryStorage():RecoveryStorage{
  try{return window.sessionStorage;}catch{return unavailable();}
}
export function taskRecoveryGeneration(){return localGeneration;}
export function sessionTaskRecoveryClosed(lane:string){return closedSessions.has(lane.split(':',1)[0]);}
export function taskRecoveryScope(storage:RecoveryStorage){
  try{
    const generation=storage.getItem(generationKey)??'initial',owner=storage.getItem(ownerKey);
    if((expectedOwner!==undefined && owner!==expectedOwner) || (generation!=='initial' && !uuid.test(generation)) || (owner!==null && (!owner || owner.length>200)))return unavailable();
    return {owner,generation};
  }catch{return unavailable();}
}
const keyFor=(lane:string)=>recordPrefix+encodeURIComponent(lane);
export function readTaskRecovery(storage:RecoveryStorage,lane:string):TaskRecoveryRecord|null{
  try{
    const raw=storage.getItem(keyFor(lane));if(raw===null)return null;
    if(raw.length>1024)return unavailable();
    const value=JSON.parse(raw),scope=taskRecoveryScope(storage);
    if(value?.version!==1 || value.lane!==lane || typeof value.fingerprint!=='string' || !/^[a-f0-9]{64}$/.test(value.fingerprint)
      || typeof value.requestId!=='string' || !uuid.test(value.requestId) || value.owner!==scope.owner || value.generation!==scope.generation
      || Object.keys(value).some(key=>!['version','lane','fingerprint','requestId','owner','generation'].includes(key)))return unavailable();
    return value;
  }catch{return unavailable();}
}
export function writeTaskRecovery(storage:RecoveryStorage,record:TaskRecoveryRecord){
  try{const raw=JSON.stringify(record);if(raw.length>1024)return unavailable();storage.setItem(keyFor(record.lane),raw);if(storage.getItem(keyFor(record.lane))!==raw)return unavailable();}
  catch{return unavailable();}
}
export function removeTaskRecovery(storage:RecoveryStorage,lane:string){
  try{storage.removeItem(keyFor(lane));if(storage.getItem(keyFor(lane))!==null)return unavailable();}
  catch{return unavailable();}
}
// Cleanup must not turn successful logout/completion into an application failure.
export function clearSessionTaskRecovery(sessionId?:string,provided?:RecoveryStorage){
  if(sessionId===undefined){localGeneration++;expectedOwner=undefined;closedSessions.clear();}
  else closedSessions.add(sessionId);
  try{
    const storage=provided??browserTaskRecoveryStorage(),keys:string[]=[];
    for(let index=0;index<storage.length;index++){const key=storage.key(index);if(key && (key.startsWith(recordPrefix)||key.startsWith(applicationPrefix)))keys.push(key);}
    for(const key of keys){
      if(sessionId===undefined || (key.startsWith(keyFor(sessionId+':')) || key.startsWith(applicationPrefix+encodeURIComponent(sessionId+':'))))storage.removeItem(key);
    }
    if(sessionId===undefined){storage.setItem(generationKey,crypto.randomUUID());storage.removeItem(ownerKey);}
  }catch{/* Requests still fail closed if their recovery state cannot be read or removed. */}
}
export function bindSessionTaskRecoveryOwner(owner:string,provided?:RecoveryStorage){
  try{
    const storage=provided??browserTaskRecoveryStorage(),previous=storage.getItem(ownerKey);
    if(previous!==owner){clearSessionTaskRecovery(undefined,storage);storage.setItem(ownerKey,owner);}
    expectedOwner=owner;
  }catch{expectedOwner=owner;/* Authentication remains available; task checkpoints enforce storage availability. */}
}

export type ModelApplicationHint={version:1;sessionId:string;requestId:string;purpose:'interview_start'|'interview_next_question';createdAt:number;owner:string|null;generation:string};
function applicationKey(sessionId:string,requestId:string){return applicationPrefix+encodeURIComponent(sessionId+':'+requestId);}
export function stageModelApplicationHint(sessionId:string,requestId:string,purpose:ModelApplicationHint['purpose'],provided?:RecoveryStorage){
  if(sessionTaskRecoveryClosed(sessionId+':'+purpose) || !uuid.test(requestId))return unavailable();
  try{
    const storage=provided??browserTaskRecoveryStorage(),scope=taskRecoveryScope(storage),key=applicationKey(sessionId,requestId),previous=storage.getItem(key);
    const hint:ModelApplicationHint={version:1,sessionId,requestId,purpose,createdAt:Date.now(),...scope};
    if(previous!==null){const existing=modelApplicationHints(sessionId,storage).find(item=>item.requestId===requestId);if(!existing || existing.purpose!==purpose)return unavailable();return;}
    if(modelApplicationHints(sessionId,storage).length>=1000)return unavailable();
    const raw=JSON.stringify(hint);if(raw.length>1024)return unavailable();storage.setItem(key,raw);if(storage.getItem(key)!==raw)return unavailable();
  }catch{return unavailable();}
}
export function modelApplicationHints(sessionId:string,provided?:RecoveryStorage):ModelApplicationHint[]{
  if(sessionTaskRecoveryClosed(sessionId+':application'))return [];
  try{
    const storage=provided??browserTaskRecoveryStorage(),scope=taskRecoveryScope(storage),hints:ModelApplicationHint[]=[];
    for(let index=0;index<storage.length;index++){
      const key=storage.key(index);if(!key?.startsWith(applicationPrefix+encodeURIComponent(sessionId+':')))continue;
      const raw=storage.getItem(key);if(!raw || raw.length>1024)return unavailable();const value=JSON.parse(raw);
      if(value?.version!==1 || value.sessionId!==sessionId || typeof value.requestId!=='string' || !uuid.test(value.requestId)
        || !['interview_start','interview_next_question'].includes(value.purpose) || !Number.isSafeInteger(value.createdAt) || value.createdAt<0
        || value.owner!==scope.owner || value.generation!==scope.generation || key!==applicationKey(sessionId,value.requestId)
        || Object.keys(value).some(field=>!['version','sessionId','requestId','purpose','createdAt','owner','generation'].includes(field)))return unavailable();
      hints.push(value);if(hints.length>1000)return unavailable();
    }
    return hints.sort((a,b)=>b.createdAt-a.createdAt || b.requestId.localeCompare(a.requestId));
  }catch{return unavailable();}
}
export function clearModelApplicationHint(sessionId:string,requestId:string,provided?:RecoveryStorage){
  try{const storage=provided??browserTaskRecoveryStorage(),key=applicationKey(sessionId,requestId);storage.removeItem(key);if(storage.getItem(key)!==null)return unavailable();}
  catch{return unavailable();}
}
